import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button as MuiButton, Typography } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { VendorsListingService, vendorOrdersService, Order, handleApiError } from '../../services';
import { VendorOrdersTable } from '../../components/Tables';
import { Vendor } from '../../types';
import { Tabs, Dropdown, Pagination, Modal, type Tab, type DropdownOption } from '../../components/Common';
import { PageHeader } from '../../components/Layout';
import { useSmartPageSize } from '../../config/pagination';
import { toast } from 'react-toastify';


const VendorOrders: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  
  // Determine active tab based on current route
  const getActiveTabFromRoute = useCallback((): 'profile' | 'products' | 'orders' | 'transactions' => {
    const path = location.pathname;
    if (path.includes('/transactions')) return 'transactions';
    if (path.includes('/orders')) return 'orders';
    if (path.includes('/products')) return 'products';
    if (path.includes('/vendors/') && !path.includes('/orders') && !path.includes('/products') && !path.includes('/transactions')) return 'profile';
    return 'orders'; // default
  }, [location.pathname]);
  
  const [activeTab, setActiveTab] = useState<'profile' | 'products' | 'orders' | 'transactions'>(() => {
    const path = location.pathname;
    if (path.includes('/transactions')) return 'transactions';
    if (path.includes('/orders')) return 'orders';
    if (path.includes('/products')) return 'products';
    if (path.includes('/vendors/') && !path.includes('/orders') && !path.includes('/products') && !path.includes('/transactions')) return 'profile';
    return 'orders'; // default
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [statusFilter, setStatusFilter] = useState<string>('Any status');
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [statusFilterAnchor, setStatusFilterAnchor] = useState<null | HTMLElement>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState<any>(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    totalPages: number;
    totalItems: number;
    hasMore: boolean;
  }>({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalItems: 0,
    hasMore: false,
  });

  // Smart pagination based on screen size
  const smartPageSize = useSmartPageSize('orders');

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditVendor = () => {
    // TODO: Implement edit vendor
    handleMenuClose();
  };

  const handleViewProfile = () => {
    navigate(`/vendors/${id}`);
    handleMenuClose();
  };

  const statusOptions: DropdownOption[] = [
    { label: 'Any status', value: 'Any status', onClick: () => { setStatusFilter('Any status'); setStatusFilterAnchor(null); setCurrentPage(1); } },
    { label: 'Pending', value: 'Pending', onClick: () => { setStatusFilter('Pending'); setStatusFilterAnchor(null); setCurrentPage(1); } },
    { label: 'Approved', value: 'Approved', onClick: () => { setStatusFilter('Approved'); setStatusFilterAnchor(null); setCurrentPage(1); } },
    { label: 'Cancelled', value: 'Cancelled', onClick: () => { setStatusFilter('Cancelled'); setStatusFilterAnchor(null); setCurrentPage(1); } },
    { label: 'Declined', value: 'Declined', onClick: () => { setStatusFilter('Declined'); setStatusFilterAnchor(null); setCurrentPage(1); } },
    { label: 'Refunded', value: 'Refunded', onClick: () => { setStatusFilter('Refunded'); setStatusFilterAnchor(null); setCurrentPage(1); } },
    { label: 'Changing', value: 'Changing', onClick: () => { setStatusFilter('Changing'); setStatusFilterAnchor(null); setCurrentPage(1); } },
    { label: 'In-progress', value: 'In-progress', onClick: () => { setStatusFilter('In-progress'); setStatusFilterAnchor(null); setCurrentPage(1); } },
    { label: 'Delivery', value: 'Delivery', onClick: () => { setStatusFilter('Delivery'); setStatusFilterAnchor(null); setCurrentPage(1); } },
    { label: 'Completed', value: 'Completed', onClick: () => { setStatusFilter('Completed'); setStatusFilterAnchor(null); setCurrentPage(1); } },
  ];

  const menuOptions: DropdownOption[] = [
    { label: 'Edit Vendor', value: 'edit', onClick: handleEditVendor },
    { label: 'View Profile', value: 'profile', onClick: handleViewProfile },
  ];

  const tabs: Tab[] = [
    { key: 'profile', label: 'Profile' },
    { key: 'products', label: 'Products' },
    { key: 'orders', label: 'Orders' },
    { key: 'transactions', label: 'Transactions' },
  ];

  const handleTabChange = (tabKey: string) => {
    if (tabKey === 'profile') navigate(`/vendors/${id}`);
    else if (tabKey === 'products') navigate(`/vendors/${id}/products`);
    else if (tabKey === 'orders') navigate(`/vendors/${id}/orders`);
    else if (tabKey === 'transactions') navigate(`/vendors/${id}/transactions`);
  };

  const fetchVendor = useCallback(async () => {
    if (!id) return;
    
    try {
      const vendorData = await VendorsListingService.getVendorById(id);
      setVendor(vendorData);
    } catch (error: any) {
      console.error('Error fetching vendor:', error);
    }
  }, [id]);

  const fetchOrders = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const status = statusFilter !== 'Any status' ? statusFilter : undefined;
      const response = await vendorOrdersService.getVendorOrders(id, currentPage, smartPageSize, status);
      setOrders(response.orders || []);
      if (response.pagination) {
        setPagination(response?.pagination as any);
        console.log('📄 Pagination data:', response.pagination);
      } else {
        console.log('⚠️ No pagination data in response');
        setPagination({
          page: currentPage,
          limit: smartPageSize,
          totalPages: 0,
          totalItems: response.orders?.length || 0,
          hasMore: false,
        });
      }
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [id, currentPage, statusFilter, smartPageSize]);

  // Sync activeTab with route changes
  useEffect(() => {
    setActiveTab(getActiveTabFromRoute());
  }, [getActiveTabFromRoute]);

  useEffect(() => {
    fetchVendor();
  }, [fetchVendor]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Reset to page 1 when status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const handleOrderClick = async (order: Order) => {
    setSelectedOrder(order);
    setOrderDetailsModalOpen(true);
    
    // Fetch order details
    if (id && order.id) {
      try {
        setLoadingOrderDetails(true);
        const details = await vendorOrdersService.getOrderDetails(id, order.id);
        setOrderDetails(details);
      } catch (err: any) {
        console.error('Error fetching order details:', err);
        toast.error('Failed to load order details');
      } finally {
        setLoadingOrderDetails(false);
      }
    }
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsModalOpen(false);
    setSelectedOrder(null);
    setOrderDetails(null);
  };

  return (
    <Box
      className="page-container"
      sx={{
        padding: {
          xs: "24px 16px",
          sm: "40px 40px",
          md: "40px 80px",
        },
        gap: "24px",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Page Header */}
      <PageHeader
        title="Orders"
        showIconButton={true}
        onIconButtonClick={handleMenuOpen}
      />

      {/* Tabs and Table Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignSelf: "stretch",
          gap: "32px",
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Tabs and Filter */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />

          {/* Status Filter - Only show on orders tab */}
          {activeTab === "orders" && (
            <Box>
              <MuiButton
                onClick={(e) => setStatusFilterAnchor(e.currentTarget)}
                sx={{
                  backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                  color: 'var(--palette-light-text-primary-value-hex)',
                  fontSize: 'var(--palette-light-font-size-default-value)',
                  height: '39px',
                  padding: '10px 20px 7px',
                  borderRadius: '16px',
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  maxWidth: '200px',
                  '&:hover': {
                    backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                    opacity: 0.9,
                  },
                  '& .MuiButton-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  },
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                  {statusFilter}
                </span>
                <KeyboardArrowDownIcon sx={{ fontSize: '20px', flexShrink: 0 }} />
              </MuiButton>
              <Dropdown
                anchorEl={statusFilterAnchor}
                open={Boolean(statusFilterAnchor)}
                onClose={() => setStatusFilterAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                options={statusOptions}
              />
            </Box>
          )}
        </Box>

        {/* Tab Content */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden'
          }}
        >
          {activeTab === "orders" && id && (
            <VendorOrdersTable 
              orders={orders} 
              loading={loading}
              onOrderClick={handleOrderClick}
            />
          )}
        </Box>
      </Box>

      {/* Pagination */}
      {activeTab === "orders" && !loading && !error && pagination.totalPages > 0 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: 'var(--palette-light-6-value)', // 24px
          mt: 'var(--palette-light-6-value)', // 24px
        }}>
          <Pagination
            currentPage={currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            itemsPerPage={smartPageSize}
            onPageChange={setCurrentPage}
            itemLabel="orders"
            showItemCount={false}
            showPrevNext={true}
          />
          {/* Item Count on the right */}
          {pagination.totalItems > 0 && (
            <Typography sx={{
              fontSize: 'var(--palette-light-font-size-default-value)',
              color: 'var(--palette-light-text-secondary-value-hex)',
            }}>
              {(() => {
                const start = (currentPage - 1) * smartPageSize + 1;
                const end = Math.min(currentPage * smartPageSize, pagination.totalItems);
                return `${start}–${end} of ${pagination.totalItems} orders`;
              })()}
            </Typography>
          )}
        </Box>
      )}

      {/* Action Menu Dropdown */}
      <Dropdown
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        options={menuOptions}
      />

      {/* Order Details Modal */}
      <Modal
        open={orderDetailsModalOpen}
        onClose={handleCloseOrderDetails}
        title={selectedOrder ? `Order #${selectedOrder.id || selectedOrder.orderNumber || 'N/A'}` : 'Order Details'}
        width={800}
      >
        {loadingOrderDetails ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Typography>Loading order details...</Typography>
          </Box>
        ) : orderDetails && selectedOrder ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Event Information */}
            <Box>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)', mb: '8px' }}>
                Event date
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                {selectedOrder.eventDate || 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)', mb: '8px' }}>
                Contact person
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                {selectedOrder.customerName || 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)', mb: '8px' }}>
                Phone number
              </Typography>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                {selectedOrder.customerPhone || 'N/A'}
              </Typography>
            </Box>

            {/* Product/Service Ordered */}
            <Box>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 600, color: 'var(--palette-light-text-primary-value-hex)', mb: '16px' }}>
                1 product ordered
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: '16px', 
                padding: '16px', 
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                borderRadius: '16px'
              }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 600, color: 'var(--palette-light-text-primary-value-hex)', mb: '8px' }}>
                    {selectedOrder.productName || 'N/A'}
                  </Typography>
                  <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)', mb: '8px' }}>
                    {selectedOrder.productDetails || ''}
                  </Typography>
                  {selectedOrder.additionalProducts && (
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-brand-primary-value-hex)' }}>
                      {selectedOrder.additionalProducts}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                  {selectedOrder.originalTotal ? (
                    <>
                      <Typography sx={{ 
                        fontSize: 'var(--palette-light-font-size-small-value)', 
                        color: 'var(--palette-light-text-secondary-value-hex)',
                        textDecoration: 'line-through'
                      }}>
                        {selectedOrder.originalTotal}
                      </Typography>
                      <Typography sx={{ 
                        fontSize: 'var(--palette-light-font-size-default-value)', 
                        fontWeight: 600, 
                        color: 'var(--palette-light-text-primary-value-hex)' 
                      }}>
                        {selectedOrder.total || 'N/A'}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 600, color: 'var(--palette-light-text-primary-value-hex)' }}>
                      {selectedOrder.total || 'N/A'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Total and Payment */}
            <Box sx={{ borderTop: '1px solid var(--palette-light-background-tertiary-value-hex)', paddingTop: '16px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '16px' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 600, color: 'var(--palette-light-text-primary-value-hex)' }}>
                  Total
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                  {selectedOrder.originalTotal ? (
                    <>
                      <Typography sx={{ 
                        fontSize: 'var(--palette-light-font-size-small-value)', 
                        color: 'var(--palette-light-text-secondary-value-hex)',
                        textDecoration: 'line-through'
                      }}>
                        {selectedOrder.originalTotal}
                      </Typography>
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 600, color: 'var(--palette-light-text-primary-value-hex)' }}>
                        {selectedOrder.total || 'N/A'}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 600, color: 'var(--palette-light-text-primary-value-hex)' }}>
                      {selectedOrder.total || 'N/A'}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                Order placed on {selectedOrder.orderDate}, {selectedOrder.orderTime}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Typography>No order details available</Typography>
          </Box>
        )}
      </Modal>
    </Box>
  );
};
export default VendorOrders;

